import { matchName } from '../utils/match_name.js'

const authErrorCode = 401

// In-memory cache for verified tokens (avoids RPC call per request)
const tokenCache = new Map()
const CACHE_TTL = 60_000 // 60 seconds

/**
 * Check if error is an authentication error (code 401)
 */
export const isAuthError = (error) => error?.code === authErrorCode

export const Auth = ({ getWorkerClient, match }) => {
  return (name, fn) => {
    if (!matchName(match, name)) {
      return fn
    }

    return async (ctx, ...args) => {
      // auth
      const auth_info = await authVerify(ctx, getWorkerClient)

      // inject auth info
      ctx.auth_info = auth_info

      const v = await fn(ctx, ...args)
      return v
    }
  }
}

const authVerify = async (ctx, getWorkerClient) => {
  try {
    const accessToken = getAccessTokenFromCtx(ctx)

    if (!accessToken) {
      throw new Error('no access token provided')
    }

    // Check cache first
    const cached = tokenCache.get(accessToken)
    if (cached && Date.now() < cached.expiresAt) {
      return cached.authInfo
    }

    const client = getWorkerClient()
    const auth_info = await client.callByName('user-auth-worker', 'verifyAccessToken', [
      {
        accessToken,
      },
    ])

    if (!auth_info) {
      throw new Error('invalid access token')
    }

    // Cache the result
    tokenCache.set(accessToken, { authInfo: auth_info, expiresAt: Date.now() + CACHE_TTL })
    // Evict expired entries when cache grows large
    if (tokenCache.size > 1000) {
      const now = Date.now()
      for (const [key, val] of tokenCache) {
        if (now >= val.expiresAt) tokenCache.delete(key)
      }
    }

    return auth_info
  } catch (err) {
    err.code = authErrorCode
    err.message = `auth verify failed: ${err.message || err}`

    throw err
  }
}

// Authorization: Bearer <access_token>
const getAccessTokenFromCtx = (ctx) => {
  if (!ctx.headers) {
    throw new Error('no headers in ctx')
  }
  if (!ctx.headers.authorization) {
    throw new Error('no authorization in ctx.headers')
  }

  const auth = ctx.headers.authorization.split(' ')
  if (auth.length !== 2) {
    throw new Error('invalid authorization format')
  }
  if (auth[0] !== 'Bearer') {
    throw new Error('invalid authorization type, expected Bearer')
  }

  return auth[1]
}
